// src/components/ContactSection.jsx
import { useThemeColors } from "../context/ThemeColorContext";
import { ThemeContext } from "../context/ThemeContext";
import { useStaticTexts } from "../context/StaticTextContext";
import { useContext, useEffect, useState } from "react";
import PopupMessage from "./PopupMessage";
import axios from "axios";

const ContactSection = () => {
  const { darkMode } = useContext(ThemeContext);
  const {
    darkSecondary,
    lightSecondary,
    darkText,
    lightText,
    darkPrimary,
    darkAccent,
  } = useThemeColors();

  const staticTexts = useStaticTexts();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [statusMessage, setStatusMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => {
        setStatusMessage("");
        setShowPopup(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  if (!staticTexts || Object.keys(staticTexts).length === 0) return null;

  const {
    contact_us_title,
    phone_label,
    email_label,
    address_label,
    phone_number,
    email,
    address,
    contact_profile_image,
    contact_name_placeholder,
    contact_email_placeholder,
    contact_phone_placeholder,
    contact_subject_placeholder,
    contact_message_placeholder,
    contact_submit_text,
    contact_success_message,
    contact_error_message,
  } = staticTexts;

  const textColor = darkMode ? darkText : lightText;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(false);
    setStatusMessage("");

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/contact/`, formData);
      if (res.status === 201 || res.status === 200) {
        setStatusMessage(contact_success_message);
        setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
        setShowPopup(true);
      } else {
        throw new Error();
      }
    } catch (err) {
      setError(true);
      setStatusMessage(contact_error_message);
      setShowPopup(true);
    }
  };

  const inputStyle = {
    backgroundColor: darkMode ? darkSecondary : "#fff",
    color: darkMode ? darkText : "#000",
    borderColor: darkMode ? darkPrimary : "#ccc",
  };

  return (
    <main className="p-6 max-w-5xl mx-auto">
      <div className="text-center mb-10">
        <h2 className="text-2xl font-semibold mb-2" style={{ color: lightText }}>
          {contact_us_title}
        </h2>
      </div>

      <div
        className="rounded-xl shadow p-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-center mb-10"
        style={{
          backgroundColor: darkMode ? darkSecondary : lightSecondary,
          color: textColor,
        }}
      >
        <div className="space-y-3">
          <p><strong>{phone_label}</strong> {phone_number}</p>
          <p><strong>{email_label}</strong> {email}</p>
          {address && <p><strong>{address_label}</strong> {address}</p>}
        </div>
        <div className="flex justify-center">
          <img
            src={contact_profile_image}
            alt="contact profile"
            className="w-48 h-48 object-cover rounded-full shadow-lg"
          />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            name="name"
            type="text"
            placeholder={contact_name_placeholder}
            value={formData.name}
            onChange={handleChange}
            className="p-3 rounded border w-full"
            style={inputStyle}
            required
          />
          <input
            name="email"
            type="email"
            placeholder={contact_email_placeholder}
            value={formData.email}
            onChange={handleChange}
            className="p-3 rounded border w-full"
            style={inputStyle}
            required
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            name="phone"
            type="text"
            placeholder={contact_phone_placeholder}
            value={formData.phone}
            onChange={handleChange}
            className="p-3 rounded border w-full"
            style={inputStyle}
          />
          <input
            name="subject"
            type="text"
            placeholder={contact_subject_placeholder}
            value={formData.subject}
            onChange={handleChange}
            className="p-3 rounded border w-full"
            style={inputStyle}
          />
        </div>
        <textarea
          name="message"
          rows="5"
          placeholder={contact_message_placeholder}
          value={formData.message}
          onChange={handleChange}
          className="w-full p-3 rounded border"
          style={inputStyle}
          required
        />

        <button
          type="submit"
          className="px-6 py-2 rounded-lg transition font-medium border"
          style={{
            backgroundColor: darkMode ? "#fff" : darkAccent,
            color: darkMode ? darkPrimary : "#fff",
            borderColor: darkMode ? darkPrimary : "transparent",
          }}
        >
          {contact_submit_text}
        </button>
      </form>

      {showPopup && (
        <PopupMessage
          message={statusMessage}
          error={error}
          onClose={() => {
            setStatusMessage("");
            setShowPopup(false);
          }}
        />
      )}
    </main>
  );
};

export default ContactSection;
